import React, { useState } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import './Quiz.css';

const Quiz = ({ moduleId, questions, onComplete }) => {
  const { progress, completeQuiz } = useProgress();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const moduleProgress = progress.modules[moduleId];
  const alreadyCompleted = moduleProgress?.quizCompleted;
  const previousScore = moduleProgress?.quizScore;

  const handleSelectAnswer = (questionIndex, answerIndex) => {
    if (showResults) return;
    setSelectedAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    return Math.round((correct / questions.length) * 100);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      const score = calculateScore();
      completeQuiz(moduleId, score);
      setShowResults(true);
      setIsSubmitting(false);
      if (onComplete) {
        onComplete(score);
      }
    }, 1000);
  };

  const handleRetry = () => {
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setShowResults(false);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const allAnswered = Object.keys(selectedAnswers).length === questions.length;
  const score = showResults ? calculateScore() : 0;
  const passed = score >= 70;

  if (alreadyCompleted && !showResults) {
    return (
      <div className="quiz-container">
        <div className="quiz-completed-card">
          <div className="quiz-completed-icon">
            {previousScore >= 70 ? '🏆' : '📝'}
          </div>
          <h3>Quiz déjà complété !</h3>
          <p>Votre score : <strong>{previousScore}%</strong></p>
          <p className={previousScore >= 70 ? 'passed' : 'failed'}>
            {previousScore >= 70
              ? '✅ Module validé'
              : '❌ Score insuffisant (70% requis)'}
          </p>
          <button className="btn btn-secondary" onClick={handleRetry}>
            Refaire le quiz
          </button>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="quiz-container">
        <div className={`quiz-results ${passed ? 'passed' : 'failed'}`}>
          <div className="results-header">
            <div className="results-icon">{passed ? '🎉' : '😔'}</div>
            <h2>{passed ? 'Félicitations !' : 'Pas tout à fait...'}</h2>
          </div>

          <div className="results-score">
            <div className="score-circle">
              <svg viewBox="0 0 100 100">
                <circle
                  className="score-bg"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  strokeWidth="8"
                />
                <circle
                  className="score-fill"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  strokeWidth="8"
                  strokeDasharray={`${score * 2.83} 283`}
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <span className="score-value">{score}%</span>
            </div>
            <p className="score-label">
              {passed
                ? 'Vous avez validé ce module !'
                : 'Il faut 70% pour valider. Réessayez !'}
            </p>
          </div>

          <div className="results-details">
            <h4>Récapitulatif des réponses</h4>
            {questions.map((q, index) => {
              const isCorrect = selectedAnswers[index] === q.correctAnswer;
              return (
                <div key={index} className={`result-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <div className="result-indicator">
                    {isCorrect ? '✓' : '✗'}
                  </div>
                  <div className="result-content">
                    <p className="result-question">{q.question}</p>
                    <p className="result-answer">
                      Votre réponse : {q.options[selectedAnswers[index]]}
                    </p>
                    {!isCorrect && (
                      <p className="result-correct">
                        Bonne réponse : {q.options[q.correctAnswer]}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="results-actions">
            {!passed && (
              <button className="btn btn-primary" onClick={handleRetry}>
                🔄 Réessayer le quiz
              </button>
            )}
            {passed && (
              <p className="success-message">
                ✨ Vous pouvez maintenant passer au module suivant !
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <h3>📝 Quiz du Module {moduleId}</h3>
        <div className="quiz-progress">
          <span>Question {currentQuestion + 1} sur {questions.length}</span>
          <div className="quiz-progress-bar">
            <div
              className="quiz-progress-fill"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="quiz-navigation-dots">
        {questions.map((_, index) => (
          <button
            key={index}
            className={`nav-dot ${index === currentQuestion ? 'active' : ''} ${
              selectedAnswers[index] !== undefined ? 'answered' : ''
            }`}
            onClick={() => setCurrentQuestion(index)}
          >
            {index + 1}
          </button>
        ))}
      </div>

      <div className="quiz-question-card">
        <p className="question-text">{question.question}</p>

        <div className="quiz-options">
          {question.options.map((option, index) => (
            <button
              key={index}
              className={`quiz-option ${
                selectedAnswers[currentQuestion] === index ? 'selected' : ''
              }`}
              onClick={() => handleSelectAnswer(currentQuestion, index)}
            >
              <span className="option-letter">
                {String.fromCharCode(65 + index)}
              </span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="quiz-actions">
        <button
          className="btn btn-secondary"
          onClick={handlePrev}
          disabled={currentQuestion === 0}
        >
          ← Précédent
        </button>

        {currentQuestion < questions.length - 1 ? (
          <button
            className="btn btn-primary"
            onClick={handleNext}
            disabled={selectedAnswers[currentQuestion] === undefined}
          >
            Suivant →
          </button>
        ) : (
          <button
            className={`btn btn-primary submit-btn ${isSubmitting ? 'submitting' : ''}`}
            onClick={handleSubmit}
            disabled={!allAnswered || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Vérification...
              </>
            ) : (
              '✓ Valider le quiz'
            )}
          </button>
        )}
      </div>

      <p className="quiz-hint">
        {allAnswered
          ? '✅ Toutes les questions ont une réponse. Vous pouvez valider !'
          : `⏳ ${questions.length - Object.keys(selectedAnswers).length} question(s) sans réponse`}
      </p>
    </div>
  );
};

export default Quiz;
